import { useNovel } from '@/context/novel-context';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { CommentPopover } from '@/features/comments/components/CommentPopover';
import { CommentSidebar } from '@/features/comments/components/CommentSidebar';
import { useComments } from '@/features/comments/hooks/use-comments';
import {
  AddBoxOutlined,
  FormatBold as Bold,
  Title as Heading1,
  Looks3 as Heading2,
  Highlight as Highlighter,
  FormatItalic as Italic,
  FormatListBulleted as List,
  FormatListNumbered as ListOrdered,
  FormatQuote as Quote,
  Redo,
  Spellcheck,
  FormatUnderlined as UnderlineIcon,
  Undo,
  AutoAwesome,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import CharacterCount from '@tiptap/extension-character-count';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { Fragment, Slice } from '@tiptap/pm/model';
import { Plugin, Selection, TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingBubbleMenu } from './FloatingBubbleMenu';
import { CommentExtension } from './extensions/comment-mark';
import { EntityHighlights } from './entity-highlights';
import type { EnrichedEntity } from '@/lib/types';

const CustomParagraph = Node.create({
  name: 'paragraph',
  priority: 1000,
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  group: 'block',
  content: 'inline*',
  parseHTML() {
    return [{ tag: 'p' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['p', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes: Record<string, unknown>) => {
          const commentId = attributes.commentId;
          if (!commentId || typeof commentId !== 'string') {
            return {};
          }
          return {
            'data-comment-id': commentId,
            class: 'commented-paragraph',
          };
        },
      },
    };
  },
});

const SceneSection = Node.create({
  name: 'sceneSection',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,
  addAttributes: () => ({ sceneId: { default: null, parseHTML: (element) => element.getAttribute('data-scene-id'), renderHTML: (attributes) => ({ 'data-scene-id': attributes.sceneId }) } }),
  parseHTML: () => [{
    tag: 'section[data-section-type=scene]',
    contentElement: (element) => element.querySelector('.scene-section-content') || element,
  }],
  renderHTML: ({ HTMLAttributes }) => ['section', mergeAttributes(HTMLAttributes, { 'data-section-type': 'scene', class: 'chapter-scene-section' }),
    ['span', { class: 'scene-section-gutter', contenteditable: 'false' }],
    ['div', { class: 'scene-section-content' }, 0]],
});

const InterSceneSection = Node.create({
  name: 'interSceneSection',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,
  addAttributes: () => ({ sectionId: { default: null, parseHTML: (element) => element.getAttribute('data-section-id'), renderHTML: (attributes) => ({ 'data-section-id': attributes.sectionId }) } }),
  parseHTML: () => [{ tag: 'section[data-section-type=inter-scene]' }],
  renderHTML: ({ HTMLAttributes }) => ['section', mergeAttributes(HTMLAttributes, { 'data-section-type': 'inter-scene', class: 'chapter-inter-scene-section' }), 0],
});

const SectionNavigationExtension = Extension.create({
  name: 'sectionNavigation',
  addKeyboardShortcuts() {
    const moveAcrossSection = (direction: -1 | 1) => this.editor.commands.command(({ state, dispatch }) => {
      const { selection } = state;
      if (!selection.empty) return false;
      const { $from } = selection;
      let sectionDepth = -1;
      for (let depth = $from.depth; depth > 0; depth -= 1) {
        const nodeName = $from.node(depth).type.name;
        if (nodeName === 'sceneSection' || nodeName === 'interSceneSection') {
          sectionDepth = depth;
          break;
        }
      }
      if (sectionDepth < 0) return false;

      const atBoundary = direction === 1
        ? $from.parentOffset === $from.parent.content.size
        && $from.indexAfter(sectionDepth) === $from.node(sectionDepth).childCount
        : $from.parentOffset === 0 && $from.index(sectionDepth) === 0;
      if (!atBoundary) return false;

      const boundaryPosition = direction === 1
        ? $from.after(sectionDepth)
        : $from.before(sectionDepth);
      const nextSelection = Selection.near(state.doc.resolve(boundaryPosition), direction);
      if (nextSelection.from === selection.from) return false;
      dispatch?.(state.tr.setSelection(nextSelection).scrollIntoView());
      return true;
    });

    return {
      ArrowRight: () => moveAcrossSection(1),
      ArrowDown: () => moveAcrossSection(1),
      ArrowLeft: () => moveAcrossSection(-1),
      ArrowUp: () => moveAcrossSection(-1),
    };
  },
});

const SectionBoundaryGuard = Extension.create({
  name: 'sectionBoundaryGuard',
  addStorage() {
    return { allowStructureChange: false };
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    const structureOf = (doc: typeof editor.state.doc) => {
      const sections: string[] = [];
      let containsScene = false;
      let containsNestedSection = false;
      doc.forEach((node) => {
        if (node.type.name === 'sceneSection') containsScene = true;
        sections.push(`${node.type.name}:${node.attrs.sceneId || node.attrs.sectionId || ''}`);
      });
      doc.descendants((node, position) => {
        if (['sceneSection', 'interSceneSection'].includes(node.type.name) && doc.resolve(position).depth > 0) {
          containsNestedSection = true;
        }
      });
      return containsScene ? `${containsNestedSection ? 'INVALID_NESTED|' : ''}${sections.join('|')}` : null;
    };
    const containsSectionNode = (fragment: Fragment): boolean => {
      let contains = false;
      fragment.forEach((node) => {
        if (['sceneSection', 'interSceneSection'].includes(node.type.name) || containsSectionNode(node.content)) contains = true;
      });
      return contains;
    };
    const unwrapSections = (fragment: Fragment): Fragment => {
      const nodes: typeof editor.state.doc[] = [];
      fragment.forEach((node) => {
        if (['sceneSection', 'interSceneSection'].includes(node.type.name)) {
          unwrapSections(node.content).forEach((child) => nodes.push(child));
        } else {
          nodes.push(node.isLeaf ? node : node.copy(unwrapSections(node.content)));
        }
      });
      return Fragment.fromArray(nodes);
    };
    const getSectionNodePos = ($pos: typeof editor.state.selection.$from) => {
      for (let depth = $pos.depth; depth > 0; depth -= 1) {
        const node = $pos.node(depth);
        if (['sceneSection', 'interSceneSection'].includes(node.type.name)) {
          return $pos.before(depth);
        }
      }
      return null;
    };
    const spansSections = (selection: typeof editor.state.selection) => {
      if (selection.empty) return false;
      const fromPos = getSectionNodePos(selection.$from);
      const toPos = getSectionNodePos(selection.$to);
      if (fromPos === null && toPos === null) return false;
      return fromPos !== toPos;
    };
    const deleteAcrossSections = (state: typeof editor.state, dispatch?: typeof editor.view.dispatch) => {
      const { selection } = state;
      if (selection.empty || !spansSections(selection)) return null;
      const ranges: Array<{ from: number; to: number; clearsSection: boolean }> = [];
      state.doc.forEach((node, offset) => {
        if (!['sceneSection', 'interSceneSection'].includes(node.type.name)) return;
        const contentFrom = offset + 1;
        const contentTo = offset + node.nodeSize - 1;
        const from = Math.max(selection.from, contentFrom);
        const to = Math.min(selection.to, contentTo);
        if (from < to) ranges.push({ from, to, clearsSection: from === contentFrom && to === contentTo });
      });
      if (ranges.length === 0) return null;
      const transaction = state.tr;
      for (let index = ranges.length - 1; index >= 0; index -= 1) {
        const range = ranges[index];
        if (range.clearsSection) {
          transaction.replaceWith(range.from, range.to, state.schema.nodes.paragraph.create());
        } else {
          transaction.delete(range.from, range.to);
        }
      }
      const insertionPoint = transaction.mapping.map(selection.from, -1);
      transaction.setSelection(Selection.near(transaction.doc.resolve(insertionPoint), 1));
      if (dispatch) dispatch(transaction);
      return transaction;
    };
    return [new Plugin({
      filterTransaction(transaction, state) {
        const currentStructure = structureOf(state.doc);
        if (!transaction.docChanged || currentStructure === null) return true;
        const storage = (editor.storage as unknown as Record<string, { allowStructureChange: boolean }>).sectionBoundaryGuard;
        if (storage.allowStructureChange || transaction.getMeta('allowSectionStructureChange')) return true;

        return structureOf(transaction.doc) === currentStructure;
      },
      props: {
        handleKeyDown(view, event) {
          if (!['Backspace', 'Delete'].includes(event.key) || !spansSections(view.state.selection)) return false;
          const transaction = deleteAcrossSections(view.state, view.dispatch);
          return Boolean(transaction);
        },
        handleTextInput(view, _from, _to, text) {
          if (!spansSections(view.state.selection)) return false;
          const transaction = deleteAcrossSections(view.state);
          if (!transaction) return false;
          view.dispatch(transaction.insertText(text).scrollIntoView());
          return true;
        },
        handlePaste(view, _event, slice) {
          if (!spansSections(view.state.selection)) return false;
          const transaction = deleteAcrossSections(view.state);
          if (!transaction) return false;
          const safeSlice = containsSectionNode(slice.content)
            ? new Slice(unwrapSections(slice.content), 0, 0)
            : slice;
          view.dispatch(transaction.replaceSelection(safeSlice).scrollIntoView());
          return true;
        },
      },
    })];
  },
});

const PERSISTED_TAB = '\u00a0\u00a0\u00a0\u00a0';

const TabExtension = Extension.create({
  name: 'tabIndent',
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        const { state } = editor;
        let inList = false;
        state.doc.nodesBetween(state.selection.from, state.selection.to, (node) => {
          if (node.type.name === 'listItem') inList = true;
        });
        if (inList) return false;

        if (state.selection.empty) {
          return editor.commands.insertContent(PERSISTED_TAB);
        }

        return editor.commands.command(({ tr, state, dispatch }) => {
          const { doc, selection } = state;
          const positions: number[] = [];

          doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.isTextblock) {
              positions.push(pos + 1);
            }
          });

          if (dispatch) {
            for (let i = positions.length - 1; i >= 0; i--) {
              tr.insertText(PERSISTED_TAB, positions[i]);
            }
          }
          return true;
        });
      },
      'Shift-Tab': ({ editor }) => {
        const { state } = editor;
        let inList = false;
        state.doc.nodesBetween(state.selection.from, state.selection.to, (node) => {
          if (node.type.name === 'listItem') inList = true;
        });
        if (inList) return false;

        return editor.commands.command(({ tr, state, dispatch }) => {
          const { doc, selection } = state;
          const removals: Array<{ position: number; length: number }> = [];

          doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.isTextblock) {
              const text = node.firstChild?.isText ? node.firstChild.text || '' : '';
              if (text.startsWith(PERSISTED_TAB)) {
                removals.push({ position: pos + 1, length: PERSISTED_TAB.length });
              } else if (text.startsWith('\t')) {
                removals.push({ position: pos + 1, length: 1 });
              }
            }
          });

          if (removals.length === 0) return false;

          if (dispatch) {
            for (let i = removals.length - 1; i >= 0; i--) {
              tr.delete(removals[i].position, removals[i].position + removals[i].length);
            }
          }
          return true;
        });
      },
    };
  },
});

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onCommentChange?: (content: string) => void | Promise<void>;
  onSave?: (content?: string, silent?: boolean, skipHistory?: boolean) => Promise<void>;
  placeholder?: string;
  textId?: string | null;
  sectionControls?: boolean;
  sectionLabels?: Record<string, string>;
  entityHighlights?: boolean;
  entities?: EnrichedEntity[];
}

export function RichTextEditor({ content, onChange, onCommentChange, placeholder, textId = null, sectionControls = false, sectionLabels = {}, entityHighlights = false, entities = [] }: RichTextEditorProps) {
  const { t } = useTranslation();
  const { novel } = useNovel();
  const { userData, updateProfile } = useUserProfile();
  const { comments, addComment, resolveComment, deleteComment } = useComments(textId);
  const [newCommentAnchor, setNewCommentAnchor] = useState<{ top: number; left: number; text: string } | null>(null);
  const [spellcheckEnabled, setSpellcheckEnabled] = useState(true);
  const [highlightsEnabled, setHighlightsEnabled] = useState(false);
  const [rightClickOpen, setRightClickOpen] = useState(false);
  const editorRef = useRef<(NonNullable<ReturnType<typeof useEditor>> & { showOnRightClick?: boolean }) | null>(null);
  const entityListRef = useRef<EnrichedEntity[]>(entities);
  const highlightsEnabledRef = useRef(highlightsEnabled);
  highlightsEnabledRef.current = highlightsEnabled;
  const commentTransactionRef = useRef(false);
  entityListRef.current = entities;

  useEffect(() => {
    if (userData.entityHighlightsEnabled !== undefined) {
      setHighlightsEnabled(userData.entityHighlightsEnabled);
    }
  }, [userData.entityHighlightsEnabled]);

  const languageCode = useMemo(() => {
    switch (novel?.language) {
      case 'Catalan': return 'ca';
      case 'French': return 'fr';
      case 'German': return 'de';
      case 'Italian': return 'it';
      case 'Spanish': return 'es';
      case 'English': return 'en';
      default: return 'en';
    }
  }, [novel?.language]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
      }),
      TabExtension,
      CustomParagraph,
      SceneSection,
      InterSceneSection,
      SectionNavigationExtension,
      SectionBoundaryGuard,
      Underline,
      Highlight,
      Typography,
      CommentExtension,
      Placeholder.configure({
        placeholder: placeholder || t('editor.start_writing'),
      }),
      CharacterCount.configure({
        limit: 500000,
      }),
      EntityHighlights.configure({ enabled: entityHighlights, entities }),
    ],
    content,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        spellcheck: spellcheckEnabled ? 'true' : 'false',
        lang: languageCode,
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          if (event.button === 0) {
            setRightClickOpen(false);
            const editorInstance = editorRef.current;
            if (editorInstance) {
              editorInstance.showOnRightClick = false;
            }
          }
          return false;
        },
        keydown: () => {
          setRightClickOpen(false);
          const editorInstance = editorRef.current;
          if (editorInstance) {
            editorInstance.showOnRightClick = false;
          }
          return false;
        },
        contextmenu: (view, event) => {
          event.preventDefault();

          const editorInstance = editorRef.current;
          if (editorInstance) {
            editorInstance.showOnRightClick = true;
          }

          if (view.state.selection.empty) {
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
            if (pos) {
              const currentPos = view.state.selection.from;

              if (currentPos === pos.pos) {
                // To force TipTap BubbleMenu to re-evaluate shouldShow when clicked at same pos,
                // we temporarily select a nearby position and then select the actual position.
                const tempPos = pos.pos > 0 ? pos.pos - 1 : (pos.pos < view.state.doc.content.size ? pos.pos + 1 : pos.pos);

                if (tempPos !== pos.pos) {
                  const tr1 = view.state.tr;
                  view.dispatch(tr1.setSelection(TextSelection.create(view.state.doc, tempPos, tempPos)));
                }

                const tr2 = view.state.tr;
                view.dispatch(tr2.setSelection(TextSelection.create(view.state.doc, pos.pos, pos.pos)));
              } else {
                const tr = view.state.tr;
                view.dispatch(tr.setSelection(TextSelection.create(view.state.doc, pos.pos, pos.pos)));
              }
            }
          }

          setRightClickOpen(true);

          if (editorInstance) {
            editorInstance.emit('selectionUpdate', {
              editor: editorInstance,
              transaction: editorInstance.state.tr,
            });
          }

          return true;
        },
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (commentTransactionRef.current) {
        commentTransactionRef.current = false;
        void onCommentChange?.(html);
        return;
      }
      onChange(html);
    },
  });

  // Apply safe remote collaboration updates received through the content prop.
  // emitUpdate=false prevents a remote snapshot from being reported as a local edit.
  useEffect(() => {
    if (editor && editor.getHTML() !== content) {
      const storage = (editor.storage as unknown as Record<string, { allowStructureChange: boolean }>).sectionBoundaryGuard;
      storage.allowStructureChange = true;
      try {
        editor.commands.setContent(content, { emitUpdate: false });
      } finally {
        storage.allowStructureChange = false;
      }
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    const updateSectionMetadata = () => {
      const sceneIds = Object.keys(sectionLabels);
      editor.view.dom.querySelectorAll<HTMLElement>('.chapter-scene-section').forEach((element, index) => {
        const sceneId = element.dataset.sceneId || sceneIds[index];
        const label = (sceneId && sectionLabels[sceneId]) || `${index + 1} · Scene`;
        element.dataset.sectionLabel = label;
        const sceneIndex = sceneId ? Object.keys(sectionLabels).indexOf(sceneId) : index;
        const hue = ((sceneIndex < 0 ? index : sceneIndex) * 67 + 28) % 360;
        element.style.setProperty('--scene-section-color', `hsl(${hue} 72% 52%)`);
        const gutter = element.querySelector<HTMLElement>('.scene-section-gutter');
        if (gutter) {
          gutter.dataset.sectionLabel = label;
        }
      });
      editor.view.dom.querySelectorAll<HTMLElement>('section[data-section-type=inter-scene]').forEach((element) => {
        element.dataset.sectionLabel = t('chapters.publish.inter_scene');
      });
    };

    updateSectionMetadata();
    editor.on('transaction', updateSectionMetadata);
    return () => {
      editor.off('transaction', updateSectionMetadata);
    };
  }, [editor, content, sectionLabels, t]);

  useEffect(() => {
    if (!editor) return;
    const editorElement = editor.view.dom;
    const tooltip = document.createElement('div');
    tooltip.setAttribute('role', 'tooltip');
    Object.assign(tooltip.style, {
      position: 'fixed',
      display: 'none',
      zIndex: '99999',
      maxWidth: '320px',
      padding: '8px 12px',
      borderRadius: '4px',
      background: '#212121',
      color: '#fff',
      border: '1px solid #ed8500',
      boxShadow: '0 3px 8px rgb(0 0 0 / 35%)',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '1.4',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    });
    document.body.appendChild(tooltip);

    const handlePointerMove = (event: PointerEvent) => {
      const target = event.target instanceof Element
        ? event.target.closest<HTMLElement>('.scene-section-gutter')
        : null;
      if (!target) {
        tooltip.style.display = 'none';
        return;
      }
      const section = target.closest<HTMLElement>('.chapter-scene-section');
      const sections = Array.from(editorElement.querySelectorAll('.chapter-scene-section'));
      const index = section ? sections.indexOf(section) : -1;
      const sceneId = section?.dataset.sceneId || Object.keys(sectionLabels)[index];
      const label = target.dataset.sectionLabel
        || (sceneId && sectionLabels[sceneId])
        || `${index + 1} · Scene`;
      tooltip.textContent = label;
      tooltip.style.borderColor = section
        ? getComputedStyle(section).getPropertyValue('--scene-section-color').trim() || '#ed8500'
        : '#ed8500';
      tooltip.style.left = `${event.clientX + 14}px`;
      tooltip.style.top = `${event.clientY + 14}px`;
      tooltip.style.display = 'block';
    };
    const hideTooltip = () => {
      tooltip.style.display = 'none';
    };
    editorElement.addEventListener('pointermove', handlePointerMove);
    editorElement.addEventListener('pointerleave', hideTooltip);
    return () => {
      editorElement.removeEventListener('pointermove', handlePointerMove);
      editorElement.removeEventListener('pointerleave', hideTooltip);
      tooltip.remove();
    };
  }, [editor, sectionLabels]);

  useEffect(() => {
    if (!editor || !entityHighlights) return;
    const root = editor.view.dom;
    let timer: number | undefined;
    let activeMention: HTMLElement | null = null;
    const card = document.createElement('div');
    Object.assign(card.style, {
      position: 'fixed', zIndex: '100000', display: 'none', width: '280px',
      padding: '12px', borderRadius: '10px', background: 'var(--mui-palette-background-paper, #fff)',
      color: 'var(--mui-palette-text-primary, #222)', border: '1px solid #c7782b',
      boxShadow: '0 8px 24px rgba(0,0,0,.22)', fontSize: '13px', lineHeight: '1.4',
    });
    document.body.appendChild(card);
    const hide = () => { if (timer) window.clearTimeout(timer); card.style.display = 'none'; activeMention = null; };
    const show = (mention: HTMLElement, event: PointerEvent) => {
      if (!highlightsEnabledRef.current) return;
      if (timer) window.clearTimeout(timer);
      activeMention = mention;
      timer = window.setTimeout(() => {
        const entity = entityListRef.current.find((item) => item.id === mention.dataset.entityId);
        if (!entity) return;
        const typeLabel = entity.entityType === 'characters' ? 'Character' : entity.entityType === 'locations' ? 'Location' : 'Organization';
        card.innerHTML = `<strong style="display:block;font-size:15px;margin-bottom:3px">${entity.name || ''}</strong><span style="opacity:.7;display:block;margin-bottom:7px">${typeLabel}</span><div>${entity.shortDescription || 'No description available.'}</div>`;
        const rect = mention.getBoundingClientRect();
        card.style.left = `${Math.min(rect.left, window.innerWidth - 300)}px`;
        card.style.top = `${Math.min(rect.bottom + 8, window.innerHeight - 180)}px`;
        card.style.display = 'block';
      }, 300);
    };
    const handleOver = (event: Event) => {
      const pointerEvent = event as PointerEvent;
      const target = pointerEvent.target instanceof Element ? pointerEvent.target.closest<HTMLElement>('.entity-mention') : null;
      if (target) show(target, pointerEvent);
    };
    const handleOut = (event: Event) => {
      const pointerEvent = event as PointerEvent;
      const related = pointerEvent.relatedTarget;
      if (related instanceof globalThis.Node && (card.contains(related) || activeMention?.contains(related))) return;
      hide();
    };
    root.addEventListener('pointerover', handleOver);
    root.addEventListener('pointerout', handleOut);
    const keepOpen = () => { if (timer) window.clearTimeout(timer); };
    card.addEventListener('pointerover', keepOpen);
    card.addEventListener('pointerleave', hide);
    return () => { if (timer) window.clearTimeout(timer); root.removeEventListener('pointerover', handleOver); root.removeEventListener('pointerout', handleOut); card.removeEventListener('pointerover', keepOpen); card.remove(); };
  }, [editor, entityHighlights]);

  // Update editor attributes when spellcheck or language changes
  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
      editor.setOptions({
        editorProps: {
          attributes: {
            spellcheck: spellcheckEnabled ? 'true' : 'false',
            lang: languageCode,
          },
        },
      });
    }
  }, [editor, spellcheckEnabled, languageCode]);

  useEffect(() => {
    if (!editor || !entityHighlights) return;
    const extension = editor.extensionManager.extensions.find((item) => item.name === 'entityHighlights');
    if (extension) {
      extension.options.enabled = highlightsEnabled;
      extension.options.entities = entities;
      editor.view.dispatch(editor.state.tr.setMeta('entityHighlightsToggle', true));
    }
  }, [editor, highlightsEnabled, entityHighlights, entities]);

  const handleAddCommentAction = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;

    const text = from === to ? '' : editor.state.doc.textBetween(from, to);
    let coords;
    try {
      coords = editor.view.coordsAtPos(from);
    } catch (e) {
      coords = { top: 0, left: 0 };
    }
    const editorBounds = editor.view.dom.getBoundingClientRect();

    setNewCommentAnchor({
      top: coords.top - editorBounds.top,
      left: coords.left - editorBounds.left,
      text
    });
  };

  const insertInterSceneSection = () => {
    if (!editor) return;
    const type = editor.state.schema.nodes.interSceneSection;
    const node = type?.createAndFill({ sectionId: `inter-scene-${crypto.randomUUID()}` });
    if (!node) return;
    const { $from } = editor.state.selection;
    let depth = $from.depth;
    while (depth > 0 && !['sceneSection', 'interSceneSection'].includes($from.node(depth).type.name)) depth--;
    const position = depth > 0 ? $from.after(depth) : editor.state.doc.content.size;
    editor.view.dispatch(editor.state.tr
      .setMeta('allowSectionStructureChange', true)
      .insert(position, node));
  };

  const handleSaveNewComment = async (commentContent: string) => {
    if (!newCommentAnchor || !addComment) return;

    const commentId = await addComment(commentContent);
    if (commentId && editor) {
      const { from, to } = editor.state.selection;
      commentTransactionRef.current = true;
      if (from === to) {
        const { $from } = editor.state.selection;
        let depth = $from.depth;
        let pos = $from.before(depth);
        let node = $from.node(depth);

        while (depth > 0 && node && node.type.name !== 'paragraph') {
          depth--;
          pos = $from.before(depth);
          node = $from.node(depth);
        }

        if (node && node.type.name === 'paragraph') {
          const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            commentId,
          });
          editor.view.dispatch(tr);
        }
      } else {
        (editor.chain().focus()).setComment(commentId).run();
      }

    }
    setNewCommentAnchor(null);
  };

  const removeCommentMark = (commentId: string) => {
    if (!editor) return;

    const { tr } = editor.state;
    let modified = false;

    editor.state.doc.descendants((node, pos) => {
      const mark = node.marks.find(m =>
        m.type.name === 'comment' && m.attrs.commentId === commentId
      );
      if (mark) {
        tr.removeMark(pos, pos + node.nodeSize, mark.type);
        modified = true;
      }

      if (node.type.name === 'paragraph' && node.attrs && node.attrs.commentId === commentId) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          commentId: null,
        });
        modified = true;
      }
    });

    if (modified) {
      commentTransactionRef.current = true;
      editor.view.dispatch(tr);
    }
  };

  const handleResolveComment = async (id: string) => {
    removeCommentMark(id);
    await resolveComment(id);
  };

  const handleDeleteComment = async (id: string) => {
    removeCommentMark(id);
    await deleteComment(id);
  };



  if (!editor) {
    return null;
  }

  return (
    <Box className={highlightsEnabled ? 'entity-highlights-enabled' : 'entity-highlights-disabled'} sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', '&.entity-highlights-disabled .entity-mention': { color: 'inherit !important', fontWeight: 'inherit !important', textDecoration: 'none !important', cursor: 'text' } }}>
      {/* Permanent Toolbar */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          p: 1,
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderRadius: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Tooltip title={`${t('editor.bold')} (Ctrl+B)`}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'primary' : 'default'}
          >
            <Bold sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={`${t('editor.italic')} (Ctrl+I)`}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'primary' : 'default'}
          >
            <Italic sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={`${t('editor.underline')} (Ctrl+U)`}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            color={editor.isActive('underline') ? 'primary' : 'default'}
          >
            <UnderlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title={t('editor.heading_1')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
          >
            <Heading1 sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('editor.heading_2')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
          >
            <Heading2 sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title={t('editor.bullet_list')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={editor.isActive('bulletList') ? 'primary' : 'default'}
          >
            <List sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('editor.ordered_list')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={editor.isActive('orderedList') ? 'primary' : 'default'}
          >
            <ListOrdered sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('editor.blockquote')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            color={editor.isActive('blockquote') ? 'primary' : 'default'}
          >
            <Quote sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title={t('editor.highlight')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            color={editor.isActive('highlight') ? 'primary' : 'default'}
          >
            <Highlighter sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {sectionControls && <Tooltip title={t('chapters.publish.add_inter_scene')}>
          <IconButton size='small' onClick={insertInterSceneSection}>
            <AddBoxOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>}

        <Tooltip title={spellcheckEnabled ? t('editor.disable_spellcheck') : t('editor.enable_spellcheck')}>
          <IconButton
            size="small"
            onClick={() => setSpellcheckEnabled(!spellcheckEnabled)}
            color={spellcheckEnabled ? 'primary' : 'default'}
          >
            <Spellcheck sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {entityHighlights && <Tooltip title="Entity highlights">
          <IconButton
            size="small"
            onClick={() => {
              const next = !highlightsEnabled;
              setHighlightsEnabled(next);
              void updateProfile({ entityHighlightsEnabled: next });
            }}
            color={highlightsEnabled ? 'primary' : 'default'}
            aria-label="Toggle entity highlights"
          >
            <AutoAwesome sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={t('editor.undo')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('editor.redo')}>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Floating Bubble Menu (Selection Menu) */}
      <FloatingBubbleMenu
        editor={editor}
        onAddComment={handleAddCommentAction}
        intelligenceTier={novel?.aiSettings?.intelligenceTier}
        novelId={novel?.id}
      />


      {/* New Comment Popover */}
      {newCommentAnchor && (
        <Box
          sx={{
            position: 'absolute',
            top: newCommentAnchor.top + 32, // Offset below selection
            left: Math.max(16, Math.min(newCommentAnchor.left, (editor?.view?.dom?.clientWidth || 0) - 320)),
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <CommentPopover
            isNew
            onClose={() => setNewCommentAnchor(null)}
            onSubmit={handleSaveNewComment}
          />
        </Box>
      )}

      {/* Editor Content & Sidebar Area */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.01)' : 'rgba(255,255,255,0.01)',
        color: 'text.primary',
        p: 0,
        px: { xs: 1, md: 8 }, // Add padding to accommodate gutter icons
        position: 'relative'
      }}>
        {/* Centered Wrapper for Text & Comments */}
        <Box sx={{
          maxWidth: 720, // Match .ProseMirror max-width
          mx: 'auto',
          position: 'relative',
          height: '100%'
          , '& .chapter-scene-section, & .chapter-inter-scene-section': {
            position: 'relative',
            pl: 2
          }
          , '& .chapter-scene-section': {
            '--scene-section-color': '#ed8500',
            borderLeft: '2px solid transparent',
            transition: 'border-color 120ms ease'
          }
          , '& .chapter-scene-section:nth-of-type(6n + 2)': { '--scene-section-color': '#2f9bff' }
          , '& .chapter-scene-section:nth-of-type(6n + 3)': { '--scene-section-color': '#9b6cff' }
          , '& .chapter-scene-section:nth-of-type(6n + 4)': { '--scene-section-color': '#20a978' }
          , '& .chapter-scene-section:nth-of-type(6n + 5)': { '--scene-section-color': '#e0528d' }
          , '& .chapter-scene-section:nth-of-type(6n)': { '--scene-section-color': '#d4a72c' }
          , '& .chapter-scene-section:hover, & .chapter-scene-section:focus-within': {
            borderLeftColor: 'var(--scene-section-color, #ed8500)'
          }
          , '& .scene-section-gutter': {
            position: 'absolute',
            left: -11,
            top: 0,
            bottom: 0,
            width: 20,
            cursor: 'help',
            zIndex: 1
          }
          , '& .chapter-inter-scene-section': {
            minHeight: '4em',
            pl: 2
          }
          , '& .entity-mention': {
            textDecoration: 'none !important',
            cursor: 'help',
            borderRadius: '2px',
            px: '1px'
          }
          , '& .entity-mention-characters': { textDecorationColor: 'info.main', backgroundColor: 'info.50' }
          , '& .entity-mention-locations': { textDecorationColor: 'success.main', backgroundColor: 'success.50' }
          , '& .entity-mention-organizations': { textDecorationColor: 'secondary.main', backgroundColor: 'secondary.50' }
        }}>
          <EditorContent editor={editor} style={{ height: '100%' }} />

          {/* Comment Sidebar - Now relative to the 720px centered box */}
          {editor && textId && (
            <CommentSidebar
              editor={editor}
              comments={comments}
              onResolve={handleResolveComment}
              onDelete={handleDeleteComment}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
